// Thumbnail URL resolver — no user JWT in any URL.
//
// Flow:
//   1. Sample-trip media (`sample://...`) → public CDN passthrough.
//   2. Otherwise call `public.get_media_thumbnail_signed_url(_media_id, _w)`
//      (SECURITY DEFINER, authorizes against owner / trip member / admin).
//   3. If the RPC reports `needsGenerate=true`, invoke the `thumbnail` edge
//      function via `supabase.functions.invoke()` (Authorization header sent
//      automatically by the client) to trigger generation, then re-query the
//      RPC.
//   4. Sign the returned storage path with `supabase.storage.createSignedUrl`
//      (24h). The signed URL contains a storage-object-scoped signature only;
//      it never carries the user's Supabase access/refresh token.
//   5. Cache the signed URL in memory for ~23h, keyed by `mediaId:width`.


import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { resolveSampleMediaUrl } from "@/lib/onboarding/sampleTrips";

const SIGNED_TTL_SEC = 60 * 60 * 24; // 24h server-side
const CLIENT_REFRESH_MS = 23 * 60 * 60 * 1000; // refresh ~1h before expiry

type CacheEntry = { url: string; expiresAt: number };

const urlCache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<string | null>>();

// Persist urlCache across reloads so we don't re-sign 24h URLs on cold start.
// Bounded LRU (~500 entries) in sessionStorage. Best-effort — any storage
// failure (private mode, quota) silently disables persistence.
const PERSIST_KEY = "roava:thumbUrlCache:v1";
const PERSIST_MAX = 500;
let persistDirty = false;
let persistTimer: number | null = null;

function loadPersistedCache(): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    const raw = sessionStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Array<[string, CacheEntry]>;
    if (!Array.isArray(parsed)) return;
    const now = Date.now();
    for (const entry of parsed) {
      if (!Array.isArray(entry) || entry.length !== 2) continue;
      const [k, v] = entry;
      if (typeof k !== "string" || !v || typeof v.url !== "string") continue;
      if (typeof v.expiresAt !== "number" || v.expiresAt <= now) continue;
      urlCache.set(k, v);
    }
  } catch { /* ignore */ }
}

function schedulePersist(): void {
  persistDirty = true;
  if (persistTimer != null) return;
  if (typeof window === "undefined") return;
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    if (!persistDirty) return;
    persistDirty = false;
    try {
      if (typeof sessionStorage === "undefined") return;
      // LRU-ish: keep the most recently inserted PERSIST_MAX (Map iteration is
      // insertion order).
      const entries = Array.from(urlCache.entries());
      const trimmed = entries.length > PERSIST_MAX
        ? entries.slice(entries.length - PERSIST_MAX)
        : entries;
      sessionStorage.setItem(PERSIST_KEY, JSON.stringify(trimmed));
    } catch { /* ignore quota / private mode */ }
  }, 500);
}

loadPersistedCache();

function cacheKey(mediaId: string, w: number) {
  return `${mediaId}:${w}`;
}

/** Snap to the same width buckets the RPC + edge function emit. */
export function snapThumbWidth(w: number): number {
  const buckets = [200, 400, 600, 800, 1200, 1600];
  let chosen = buckets[0];
  for (const b of buckets) if (w >= b) chosen = b;
  return chosen;
}

type ThumbRpcPayload = {
  media_id: string;
  w: number;
  path: string | null;
  needsGenerate: boolean;
  mediaType: string;
};

async function rpcLookup(
  mediaId: string,
  w: number,
): Promise<ThumbRpcPayload | null> {
  const { data, error } = await supabase.rpc("get_media_thumbnail_signed_url", {
    _media_id: mediaId,
    _w: w,
  });
  if (error) {
    // Don't log error details (could include policy hints with user ids).
    console.warn("[thumbnails] rpc lookup failed");
    return null;
  }
  return (data ?? null) as ThumbRpcPayload | null;
}

/** Async resolver: returns a signed storage URL (no user JWT) or null. */
export async function resolveThumbnailUrl(
  mediaId: string,
  w: number,
  opts?: { storagePath?: string | null },
): Promise<string | null> {
  const sample = resolveSampleMediaUrl(opts?.storagePath ?? null);
  if (sample) return sample;
  if (!mediaId) return null;

  const snapped = snapThumbWidth(w);
  const k = cacheKey(mediaId, snapped);

  const hit = urlCache.get(k);
  if (hit && hit.expiresAt > Date.now()) return hit.url;

  const pending = inflight.get(k);
  if (pending) return pending;

  const p = (async (): Promise<string | null> => {
    try {
      let payload = await rpcLookup(mediaId, snapped);
      if (!payload) return null;

      if (payload.needsGenerate) {
        // Invoke edge function via supabase client — Authorization header is
        // sent automatically; no token ever lands in a URL.
        try {
          await supabase.functions.invoke("thumbnail", {
            body: { id: mediaId, w: snapped },
          });
        } catch {
          // Soft-fail: re-query the RPC anyway in case a parallel call won.
        }
        payload = await rpcLookup(mediaId, snapped);
        if (!payload?.path) return null;
      }

      if (!payload.path) return null;

      const { data: signed, error: sErr } = await supabase.storage
        .from("trip-media")
        .createSignedUrl(payload.path, SIGNED_TTL_SEC);
      if (sErr || !signed?.signedUrl) return null;

      urlCache.set(k, {
        url: signed.signedUrl,
        expiresAt: Date.now() + CLIENT_REFRESH_MS,
      });
      schedulePersist();
      return signed.signedUrl;
    } finally {
      inflight.delete(k);
    }
  })();

  inflight.set(k, p);
  return p;
}

/** Hook variant. Returns the resolved URL (or null while loading / on error). */
export function useThumbnailUrl(
  mediaId: string | null | undefined,
  w: number,
  opts?: { storagePath?: string | null },
): { url: string | null; loading: boolean } {
  const storagePath = opts?.storagePath ?? null;
  const sample = resolveSampleMediaUrl(storagePath);

  const [state, setState] = useState<{ url: string | null; loading: boolean }>(
    () => {
      if (sample) return { url: sample, loading: false };
      if (!mediaId) return { url: null, loading: false };
      const hit = urlCache.get(cacheKey(mediaId, snapThumbWidth(w)));
      if (hit && hit.expiresAt > Date.now()) {
        return { url: hit.url, loading: false };
      }
      return { url: null, loading: true };
    },
  );

  // Bail-out ref so we don't setState after unmount / scope swap.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (sample) {
      setState({ url: sample, loading: false });
      return;
    }
    if (!mediaId) {
      setState({ url: null, loading: false });
      return;
    }
    const snapped = snapThumbWidth(w);
    const hit = urlCache.get(cacheKey(mediaId, snapped));
    if (hit && hit.expiresAt > Date.now()) {
      setState({ url: hit.url, loading: false });
      return;
    }
    setState((s) => (s.loading ? s : { url: null, loading: true }));
    let cancelled = false;
    resolveThumbnailUrl(mediaId, w, { storagePath }).then((url) => {
      if (cancelled || !aliveRef.current) return;
      setState({ url, loading: false });
    });
    return () => {
      cancelled = true;
    };
  }, [mediaId, w, storagePath, sample]);

  return state;
}
